import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Loader2, ArrowRightLeft, Copy, X, Languages } from 'lucide-react';

/**
 * Inline translator panel used inside the Chuy chat.
 * Phase 1: text-only. Uses POST /api/chuy/translate (Claude).
 */

const LANGUAGES = [
  { code: 'auto', label_es: 'Detectar', label_en: 'Auto-detect' },
  { code: 'es', label_es: 'Español', label_en: 'Spanish' },
  { code: 'en', label_es: 'Inglés', label_en: 'English' },
  { code: 'fr', label_es: 'Francés', label_en: 'French' },
  { code: 'de', label_es: 'Alemán', label_en: 'German' },
  { code: 'it', label_es: 'Italiano', label_en: 'Italian' },
  { code: 'pt', label_es: 'Portugués', label_en: 'Portuguese' },
  { code: 'zh', label_es: 'Chino', label_en: 'Chinese' },
  { code: 'ja', label_es: 'Japonés', label_en: 'Japanese' },
  { code: 'ko', label_es: 'Coreano', label_en: 'Korean' },
  { code: 'ru', label_es: 'Ruso', label_en: 'Russian' },
  { code: 'ar', label_es: 'Árabe', label_en: 'Arabic' },
  { code: 'hi', label_es: 'Hindi', label_en: 'Hindi' },
  { code: 'nl', label_es: 'Holandés', label_en: 'Dutch' },
];

export default function TranslatorPanel({ onClose, fullPage = false }) {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [source, setSource] = useState('auto');
  const [target, setTarget] = useState(language === 'es' ? 'en' : 'es');
  const [text, setText] = useState('');
  const [translated, setTranslated] = useState('');
  const [loading, setLoading] = useState(false);

  const label = (code) => {
    const l = LANGUAGES.find((x) => x.code === code);
    return l ? (language === 'es' ? l.label_es : l.label_en) : code;
  };

  const swap = () => {
    if (source === 'auto') {
      toast.message(language === 'es' ? 'Selecciona un idioma origen para invertir' : 'Pick a source language to swap');
      return;
    }
    setSource(target);
    setTarget(source);
    setText(translated);
    setTranslated(text);
  };

  const translate = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setTranslated('');
    try {
      const payload = {
        text: text.trim(),
        target_lang: target,
        source_lang: source === 'auto' ? null : source,
      };
      const { data } = await api().post('/chuy/translate', payload);
      setTranslated(data.translated || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'es' ? 'No se pudo traducir' : 'Translation failed'));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(translated);
      toast.success(language === 'es' ? 'Copiado' : 'Copied');
    } catch {
      toast.error(language === 'es' ? 'No se pudo copiar' : 'Copy failed');
    }
  };

  return (
    <div
      data-testid="chuy-translator-panel"
      className={
        fullPage
          ? 'bg-background px-4 pt-4 pb-6'
          : 'border-t border-border bg-background/95 backdrop-blur-md px-3 pt-3 pb-2 shrink-0 max-h-[55dvh] overflow-y-auto overflow-x-hidden'
      }
    >
      {/* Panel header — only render when used inline (has onClose). Full-page mode owns its own header. */}
      {!fullPage && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-caribbean font-bold text-sm min-w-0">
            <Languages size={16} className="shrink-0" />
            <span className="truncate">{language === 'es' ? 'Traductor' : 'Translator'}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="chuy-translator-close"
            className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted shrink-0"
            aria-label="Close translator"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Language selectors */}
      <div className="flex items-center gap-2 mb-2 w-full">
        <select
          data-testid="chuy-translator-source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="flex-1 min-w-0 h-9 rounded-lg border border-input bg-background px-2 text-xs"
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{label(l.code)}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={swap}
          data-testid="chuy-translator-swap"
          className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted shrink-0"
          aria-label="Swap languages"
        >
          <ArrowRightLeft size={14} />
        </button>
        <select
          data-testid="chuy-translator-target"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="flex-1 min-w-0 h-9 rounded-lg border border-input bg-background px-2 text-xs"
        >
          {LANGUAGES.filter((l) => l.code !== 'auto').map((l) => (
            <option key={l.code} value={l.code}>{label(l.code)}</option>
          ))}
        </select>
      </div>

      {/* Source textarea */}
      <textarea
        data-testid="chuy-translator-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={language === 'es' ? 'Escribe lo que quieras traducir…' : 'Type anything to translate…'}
        rows={fullPage ? 6 : 3}
        className="block w-full rounded-xl border border-input bg-background p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-caribbean/40 break-words"
      />

      {/* Translate button */}
      <button
        type="button"
        onClick={translate}
        disabled={!text.trim() || loading}
        data-testid="chuy-translator-go"
        className="mt-2 w-full h-10 rounded-full bg-caribbean hover:bg-caribbean/90 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 px-3"
      >
        {loading ? (
          <><Loader2 className="animate-spin shrink-0" size={14} /> <span className="truncate">{language === 'es' ? 'Traduciendo…' : 'Translating…'}</span></>
        ) : (
          <span className="truncate">{language === 'es' ? `Traducir a ${label(target)}` : `Translate to ${label(target)}`}</span>
        )}
      </button>

      {/* Result */}
      {translated && (
        <div
          data-testid="chuy-translator-result"
          className="mt-3 rounded-xl bg-caribbean/10 border border-caribbean/30 p-3 relative overflow-hidden"
        >
          <p className="text-[10px] uppercase tracking-wide font-bold text-caribbean mb-1 pr-8 truncate">
            {label(target)}
          </p>
          <p className="text-sm whitespace-pre-wrap break-words pr-8">{translated}</p>
          <button
            type="button"
            onClick={copy}
            data-testid="chuy-translator-copy"
            className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-caribbean hover:bg-caribbean/20 shrink-0"
            aria-label="Copy translation"
          >
            <Copy size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
